/**
 * Single source of truth for product JSON-LD.
 *
 * Rules enforced here (see schema audit, 10 Aug 2026):
 *  - No AggregateOffer for 30/60/90-day packs. One Offer for the default
 *    (30-day) pack on the canonical PDP.
 *  - Absolute, crawlable image URLs.
 *  - No invented mpn / gtin. Only the merchant-defined SKU is emitted.
 *  - Shipping + return policy, validFrom and priceValidUntil are inlined in
 *    the Offer so the Product block validates standalone in Google Shopping.

 *  - Availability is derived from the same value the visible PDP uses.
 */

import { merchantOfferExtras } from "@/lib/seo/offer-schema";
import { getPackPricingTable } from "@/lib/checkout-pricing";

export const SITE_ORIGIN = "https://truwe.in";
export const ORG_ID = `${SITE_ORIGIN}/#organization`;
export const SHIPPING_NODE_ID = `${SITE_ORIGIN}/#shipping-india`;
export const RETURNS_NODE_ID = `${SITE_ORIGIN}/#returns-india`;

/** Visible return window (policies/return-exchange-policy): 3 days from delivery. */
export const RETURN_WINDOW_DAYS = 3;

export function absoluteUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_ORIGIN}${url.startsWith("/") ? "" : "/"}${url}`;
}

/** Shared policy nodes, emitted once on the root graph. */
export function policyNodes() {
  return [
    {
      "@type": "OfferShippingDetails",
      "@id": SHIPPING_NODE_ID,
      shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "INR" },
      shippingDestination: { "@type": "DefinedRegion", addressCountry: "IN" },
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
        transitTime: { "@type": "QuantitativeValue", minValue: 2, maxValue: 5, unitCode: "DAY" },
      },
    },
    {
      "@type": "MerchantReturnPolicy",
      "@id": RETURNS_NODE_ID,
      applicableCountry: "IN",
      returnPolicyCountry: "IN",
      returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
      merchantReturnDays: RETURN_WINDOW_DAYS,
      returnMethod: "https://schema.org/ReturnByMail",
      returnFees: "https://schema.org/FreeReturn",
    },
  ] as const;
}

export interface ProductSchemaInput {
  handle: string;
  name: string;
  alternateName?: string;
  description: string;
  /** Any image reference; relative URLs are made absolute. */
  images: Array<string | null | undefined>;
  /** Price of the DEFAULT (30-day) pack, in rupees. */
  price: number | string | null | undefined;
  currency?: string;
  /** True when the default pack can actually be bought right now. */
  inStock: boolean;
  /**
   * Overrides the availability derived from `inStock`. Use "PreOrder" during a
   * pre-booking campaign (paid now, dispatched later) so the schema matches
   * what the page actually offers.
   */
  availability?: "InStock" | "OutOfStock" | "PreOrder" | "BackOrder";
  /** ISO date the pre-ordered goods ship on. Only emitted with PreOrder. */
  availabilityStarts?: string | null;
  /** Merchant-defined SKU. Omitted when absent — never invented. */
  sku?: string | null;
  aggregateRating?: { ratingValue: number; reviewCount: number } | null;
  reviews?: Array<{
    rating: number;
    title: string | null;
    comment: string | null;
    reviewer_name: string | null;
    created_at: string;
  }>;
}

export function buildProductSchema(input: ProductSchemaInput) {
  const url = `${SITE_ORIGIN}/products/${input.handle}`;
  const currency = input.currency || "INR";
  const images = input.images
    .map(absoluteUrl)
    .filter((u): u is string => Boolean(u));

  const availability =
    input.availability ?? (input.inStock ? "InStock" : "OutOfStock");

  // Price falls back to the canonical single-pack price so an Offer is never
  // dropped (a Product without offers is rejected by Google Shopping).
  const resolvedPrice =
    input.price ?? getPackPricingTable(input.handle)?.[1]?.price ?? null;

  const offer = resolvedPrice
    ? {
        "@type": "Offer",
        url,
        price: String(resolvedPrice),
        priceCurrency: currency,
        availability: `https://schema.org/${availability}`,
        ...(availability === "PreOrder" && input.availabilityStarts
          ? { availabilityStarts: input.availabilityStarts }
          : {}),
        itemCondition: "https://schema.org/NewCondition",
        seller: { "@id": ORG_ID },
        // Inlined (not @id references) so the Product block validates on its
        // own in Google Merchant / Rich Results, which read it in isolation.
        ...merchantOfferExtras(),
      }
    : undefined;


  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${url}#product`,
    name: input.name,
    ...(input.alternateName ? { alternateName: input.alternateName } : {}),
    description: input.description,
    ...(images.length ? { image: images } : {}),
    ...(input.sku ? { sku: input.sku } : {}),
    category: "Health & Beauty > Health Care > Nutritional Supplements",
    brand: { "@type": "Brand", name: "TruWe" },
    ...(input.aggregateRating && input.aggregateRating.reviewCount > 0
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: input.aggregateRating.ratingValue,
            reviewCount: input.aggregateRating.reviewCount,
            bestRating: 5,
            worstRating: 1,
          },
          ...(input.reviews?.length
            ? {
                review: input.reviews.map((r) => ({
                  "@type": "Review",
                  reviewRating: {
                    "@type": "Rating",
                    ratingValue: r.rating,
                    bestRating: 5,
                    worstRating: 1,
                  },
                  author: { "@type": "Person", name: r.reviewer_name || "Verified customer" },
                  datePublished: r.created_at.split("T")[0],
                  ...(r.title ? { name: r.title } : {}),
                  ...(r.comment ? { reviewBody: r.comment } : {}),
                })),
              }
            : {}),
        }
      : {}),
    ...(offer ? { offers: offer } : {}),
  };
}
